import { useState } from "react";
import { Header } from "@/components/Header";
import { HeroVideo } from "@/components/HeroVideo";
import { VideoMarquee } from "@/components/VideoMarquee";
import { FollowBanner } from "@/components/FollowBanner";
import { ParentCompanySection } from "@/components/ParentCompanySection";
import { WtfSection } from "@/components/WtfSection";
import { FeatureVideo } from "@/components/FeatureVideo";
import { LandscapeCarousel } from "@/components/LandscapeCarousel";
import { PillarsSection } from "@/components/PillarsSection";
import { MeetingsSection } from "@/components/Meetings";
import { DocumentsSection } from "@/components/DocumentsSection";
import { TalkingCards } from "@/components/TalkingCards";
import { ElectorateMap, useElectorates } from "@/components/ElectorateMap";
import { MapSearch } from "@/components/MapSearch";
import { StateFilter } from "@/components/StateFilter";
import { ElectorateDetail } from "@/components/ElectorateDetail";
import { JoinForm } from "@/components/JoinForm";
import { Footer } from "@/components/Footer";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-[#FAF8F2] border border-[#D8D2C4] px-5 py-4">
    <p className="font-type text-3xl sm:text-4xl font-bold text-[#E2705A]" data-testid={testId}>
      {value}
    </p>
    <p className="text-xs uppercase tracking-wider text-[#5C6A79] mt-1">{label}</p>
  </div>
);

export default function Landing() {
  const [selected, setSelected] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [preferred, setPreferred] = useState(null);
  const [stateFilter, setStateFilter] = useState("");
  const { data: stats = [] } = useElectorates();

  const total = stats.reduce((a, s) => a + s.count, 0);
  const covered = stats.filter((s) => s.count > 0).length;
  const fullGroups = stats.reduce((a, s) => a + s.full_groups, 0);

  const pickFromSearch = (name) => {
    setSelected(name);
    setFlyTarget(name);
  };

  const join = (name) => {
    setPreferred(name);
    document.getElementById("join")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F3EFE4]">
      <Header />
      <HeroVideo />
      <VideoMarquee />
      <FollowBanner />
      <PillarsSection />
      <WtfSection />
      <LandscapeCarousel />
      <MeetingsSection />
      <div className="h-12" />
      <DocumentsSection />
      <TalkingCards />

      <section id="map" className="max-w-6xl mx-auto px-5 sm:px-8 pt-4 pb-8" data-testid="map-section">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
          <div>
            <p className="font-type text-xs uppercase tracking-[0.3em] text-[#5A7355] mb-2">All 150 federal electorates</p>
            <h2 className="font-type font-bold text-3xl sm:text-4xl text-[#1B2A41]">Find your electorate</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="F.E.R.Ms" value={total} testId="stat-total-members" />
            <Stat label="Electorates active" value={covered} testId="stat-electorates-covered" />
            <Stat label="Full groups" value={fullGroups} testId="stat-full-groups" />
          </div>
        </div>
        <div className="mb-4 max-w-2xl">
          <MapSearch onPick={pickFromSearch} />
        </div>
        <div className="mb-4">
          <StateFilter value={stateFilter} onChange={setStateFilter} selected={selected} onPick={pickFromSearch} />
        </div>
        <div className="grid lg:grid-cols-[1fr_340px] gap-5 items-stretch">
          <ElectorateMap selected={selected} onSelect={setSelected} flyTarget={flyTarget} stateFilter={stateFilter} />
          <ElectorateDetail name={selected} onJoin={join} />
        </div>
      </section>

      <section id="join" className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="join-section">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-type text-xs uppercase tracking-[0.3em] text-[#5A7355] mb-2">Take your seat</p>
            <h2 className="font-type font-bold text-3xl sm:text-4xl text-[#E2705A] leading-tight">Become a F.E.R.M for your electorate</h2>
            <p className="mt-4 text-sm sm:text-base text-[#1E2830]/85 leading-relaxed max-w-prose">
              Enter your name, email and postcode. We'll place you in the open group for your federal electorate.
              {preferred && (
                <>
                  {" "}
                  You've chosen <strong data-testid="join-preferred-electorate">{preferred}</strong> — if your postcode
                  matches, we'll pre-select it for you.
                </>
              )}
            </p>
          </div>
          <div className="bg-[#FAF8F2] p-6 sm:p-8 rounded-2xl border-2 border-[#E2705A]/40 shadow-xl">
            <JoinForm preferredElectorate={preferred} />
          </div>
        </div>
      </section>

      <FeatureVideo />

      <ParentCompanySection />

      <Footer />
    </div>
  );
}
